// Holidays API fetcher — mirrors the server module at /api/v1/holidays.
//
// The page that uses these is admin-only and deliberately uses the
// aggregated top-level routes (not the per-entity nested ones under
// /users/:uuid/holidays or /drivers/:uuid/holidays).

import { apiClient } from './client';
import { endpoints } from './endpoints';

export type HolidayEntityType = 'staff' | 'driver';
export type HolidayPeriod = 'FULL' | 'AM' | 'PM';
export type HolidayStoredStatus = 'active' | 'cancelled' | 'completed';
export type HolidayCurrentStatus = 'upcoming' | 'ongoing' | 'completed' | 'cancelled';

export interface HolidayListItem {
  uuid: string;
  entity_type: HolidayEntityType;
  entity_uuid: string;
  entity_name: string;
  holiday_start: string;
  holiday_end: string;
  period: HolidayPeriod;
  total_days: number;
  status: HolidayStoredStatus;
  current_status: HolidayCurrentStatus;
  notes: string | null;
  created_at: string;
}

export interface HolidayCounts {
  total: number;
  upcoming: number;
  ongoing: number;
  completed: number;
  cancelled: number;
}

export interface HolidayListResponse {
  holidays: HolidayListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  counts: HolidayCounts;
}

export interface ListHolidaysFilters {
  page?: number;
  limit?: number;
  entity_type?: HolidayEntityType;
  status?: HolidayStoredStatus;
  q?: string;
}

export async function listHolidays(filters: ListHolidaysFilters): Promise<HolidayListResponse> {
  const params = new URLSearchParams();
  params.set('page', String(filters.page ?? 1));
  params.set('limit', String(filters.limit ?? 50));
  if (filters.entity_type) params.set('entity_type', filters.entity_type);
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  const res = await apiClient.get<HolidayListResponse>(
    `${endpoints.holidays.list}?${params.toString()}`,
  );
  return res.data;
}

export interface CreateHolidayInput {
  entity_type: HolidayEntityType;
  entity_uuid: string;
  holiday_start: string;
  holiday_end: string;
  period?: HolidayPeriod;
  notes?: string | null;
}

export async function createHoliday(
  input: CreateHolidayInput,
): Promise<{ holiday: HolidayListItem }> {
  const res = await apiClient.post<{ holiday: HolidayListItem }, CreateHolidayInput>(
    endpoints.holidays.create,
    input,
  );
  return res.data;
}

export type HolidayAction = 'cancel' | 'reinstate';

export async function updateHolidayAction(
  uuid: string,
  action: HolidayAction,
): Promise<{ holiday: HolidayListItem }> {
  const res = await apiClient.patch<{ holiday: HolidayListItem }, { action: HolidayAction }>(
    endpoints.holidays.update(uuid),
    { action },
  );
  return res.data;
}

export async function deleteHoliday(uuid: string): Promise<void> {
  await apiClient.delete(endpoints.holidays.delete(uuid));
}

export interface BulkBankHolidaysInput {
  entity_type: HolidayEntityType;
  entity_uuids: string[] | 'all';
  dates: Array<{ date: string; title: string }>;
  period?: HolidayPeriod;
}

export interface BulkImportResult {
  created: number;
  skipped: number;
}

export async function bulkBankHolidays(
  input: BulkBankHolidaysInput,
): Promise<BulkImportResult> {
  const res = await apiClient.post<BulkImportResult, BulkBankHolidaysInput>(
    endpoints.holidays.bulkImport,
    input,
  );
  return res.data;
}
